// AdminSystemMigrationsPage.js — read-only audit view of every system
// migration that has run on boot (Yabbit KB importer, docs registrar,
// legacy KB archive, calibration scheduler, etc). Master-admin only.
//
// The point of this page is trust: you SEE what the auto-sweep did on
// the last boot without having to `tail /var/log/supervisor/backend.err.log`.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ClipboardCheck, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const relTime = (iso) => {
  if (!iso) return 'never';
  try {
    const ago = (Date.now() - new Date(iso).getTime()) / 1000;
    if (ago < 60) return `${Math.floor(ago)}s ago`;
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    return `${Math.floor(ago / 86400)}d ago`;
  } catch {
    return iso;
  }
};

// Human-readable label + description for the migration keys we know
// about. Anything not listed here still shows — just with the raw key.
const MIGRATION_META = {
  yabbit_kb_importer: {
    label: 'Yabbit KB Importer',
    blurb: 'Seeds the 5 role-specific Yabbit KBs from the JSON config bundled with the app.',
  },
  yabbit_docs_registrar: {
    label: 'Yabbit Docs Registrar',
    blurb: 'Attaches the reference PDFs / DOCX + production prompts to each Yabbit KB.',
  },
  yabbit_legacy_kbs_archive: {
    label: 'Legacy KB Auto-Archive',
    blurb: 'Archives every non-Yabbit KB in any tenant that already has 5 Yabbit role KBs seeded, so the training list only shows the new ones.',
  },
  training_program_migration: {
    label: 'Training Program Migration',
    blurb: 'Assigns each domain to either kb_generic or tfvf training mode based on its KB stack.',
  },
  manager_to_domain_supervisor: {
    label: 'Manager → Domain Supervisor',
    blurb: 'Folds legacy `role=manager` users into the canonical `domain_supervisor` role so RBAC edits only touch one role name.',
  },
  yabbit_phrase_overrides: {
    label: 'Coaching Phrase Overrides',
    blurb: 'Master-admin JSON overrides for the Yabbit Suggest-Focus phrase library. Merged on top of the shipped defaults at runtime.',
  },
};

const AdminSystemMigrationsPage = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/system-migrations`, { headers });
      setRows(r.data?.migrations || []);
    } catch (err) {
      toast.error('Failed to load system migrations');
      console.error('system-migrations error', err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto p-6" data-testid="admin-system-migrations-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
              System Migrations
            </h1>
            <p className="text-white/40 text-sm mt-0.5">
              What the boot sweep did on the most recent restart. Every row is idempotent — same input, same result.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg disabled:opacity-50"
            data-testid="refresh-migrations-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-white/40">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl px-6 py-10 text-center text-white/40">
            No migrations have run yet. Restart the backend to trigger the boot sweep.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((m) => {
              const meta = MIGRATION_META[m.key] || { label: m.key, blurb: '' };
              const isOpen = expanded[m.key];
              return (
                <div
                  key={m.key}
                  className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
                  data-testid={`migration-row-${m.key}`}
                >
                  <button
                    onClick={() => setExpanded((s) => ({ ...s, [m.key]: !isOpen }))}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5"
                    data-testid={`migration-toggle-${m.key}`}
                  >
                    <ClipboardCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-semibold text-sm truncate">{meta.label}</p>
                        <span className="text-white/25 text-[10px] font-mono">{m.key}</span>
                      </div>
                      {meta.blurb && <p className="text-white/40 text-xs mt-0.5">{meta.blurb}</p>}
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="text-emerald-300 text-xs font-semibold">{relTime(m.last_run_at)}</p>
                      <p className="text-white/25 text-[10px]">{m.last_run_at?.replace('T', ' ').slice(0, 19)} UTC</p>
                    </div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid={`migration-report-${m.key}`}>
                      {m.archived_total ? (
                        <p className="text-amber-300 text-xs mb-2">
                          Archived total (all tenants): <span className="font-bold">{m.archived_total}</span>
                        </p>
                      ) : null}
                      {m.report && Object.keys(m.report).length > 0 ? (
                        <pre className="text-[11px] text-white/60 bg-black/30 rounded-md p-3 overflow-x-auto max-h-96">
                          {JSON.stringify(m.report, null, 2)}
                        </pre>
                      ) : (
                        <p className="text-white/40 text-xs italic">No detailed report captured for this run.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSystemMigrationsPage;
